import React, { useState } from 'react';
import { 
  Plus, 
  Search, 
  ExternalLink, 
  Trash2, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Building2, 
  DollarSign, 
  Calendar, 
  User, 
  MoreVertical,
  Kanban,
  PoundSterling
} from 'lucide-react';
import { JobApplication, ApplicationStatus, JobType } from '../types';

interface ApplicationTrackerProps {
  applications: JobApplication[];
  onSaveApplication: (app: JobApplication) => void;
  onDeleteApplication: (id: string) => void;
}

const COLUMNS: { id: ApplicationStatus; title: string; color: string; border: string }[] = [
  { id: 'target', title: 'Target / Tailoring', color: 'text-slate-300 bg-slate-800/60', border: 'border-slate-700' },
  { id: 'applied', title: 'Applied / Pitched', color: 'text-sky-300 bg-sky-500/10', border: 'border-sky-500/30' },
  { id: 'interviewing', title: 'Active Interviews', color: 'text-amber-300 bg-amber-500/10', border: 'border-amber-500/30' },
  { id: 'offer', title: 'Offers / Contracts', color: 'text-emerald-300 bg-emerald-500/10', border: 'border-emerald-500/30' },
  { id: 'followup', title: 'Follow-Up Needed', color: 'text-indigo-300 bg-indigo-500/10', border: 'border-indigo-500/30' },
];

export const ApplicationTracker: React.FC<ApplicationTrackerProps> = ({
  applications,
  onSaveApplication,
  onDeleteApplication
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // New Application Form State
  const [newCompany, setNewCompany] = useState('');
  const [newRole, setNewRole] = useState('');
  const [newSalary, setNewSalary] = useState('£350/day');
  const [newJobType, setNewJobType] = useState<JobType>('Contract');
  const [newLocation, setNewLocation] = useState('UK Remote');
  const [newUrl, setNewUrl] = useState('');
  const [newContact, setNewContact] = useState('');
  const [newStatus, setNewStatus] = useState<ApplicationStatus>('applied');
  const [newNotes, setNewNotes] = useState('');

  const filteredApps = applications.filter(app => {
    const matchesSearch = app.company.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          app.role.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || app.job_type === filterType;
    return matchesSearch && matchesType;
  });

  const handleStatusChange = (app: JobApplication, nextStatus: ApplicationStatus) => {
    onSaveApplication({
      ...app,
      status: nextStatus
    });
  };

  const handleCreateApplication = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCompany || !newRole) return;

    const newApp: JobApplication = {
      id: `app_${Date.now()}`,
      company: newCompany,
      role: newRole,
      salary_range: newSalary,
      job_type: newJobType,
      location: newLocation,
      status: newStatus,
      job_url: newUrl,
      contact_person: newContact,
      contact_email: '',
      match_score: 90,
      notes: newNotes,
      applied_date: new Date().toISOString().split('T')[0]
    };

    onSaveApplication(newApp);
    setIsAddModalOpen(false);
    
    // Reset Form
    setNewCompany('');
    setNewRole('');
    setNewSalary('£350/day');
    setNewUrl('');
    setNewContact('');
    setNewNotes('');
  };

  const totalInterviews = applications.filter(a => a.status === 'interviewing').length;
  const totalOffers = applications.filter(a => a.status === 'offer').length;

  return (
    <div className="space-y-8 pb-16">
      
      {/* Header & Metrics */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800/80 pb-6">
        <div>
          <div className="inline-flex items-center space-x-2 px-2.5 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-semibold">
            <Kanban className="w-3.5 h-3.5" />
            <span>Cloudflare D1 SQL Synchronized</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white mt-1">Application Pipeline & Kanban</h1>
          <p className="text-xs sm:text-sm text-slate-400">
            Track every outreach, interview stage, and contract offer with zero friction.
          </p>
        </div>

        <button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center space-x-2 px-4 py-2.5 rounded-xl font-bold text-xs bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-400 hover:to-indigo-500 text-white shadow-lg shadow-sky-500/20 transition-all self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Add New Role</span>
        </button>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <span className="text-xs text-slate-400">Total Tracked</span>
          <div className="text-2xl font-black text-white mt-1">{applications.length}</div>
        </div>
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <span className="text-xs text-amber-400 font-medium">Active Interviews</span>
          <div className="text-2xl font-black text-amber-400 mt-1">{totalInterviews}</div>
        </div>
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <span className="text-xs text-emerald-400 font-medium">Offers / Signed</span>
          <div className="text-2xl font-black text-emerald-400 mt-1">{totalOffers}</div>
        </div>
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <span className="text-xs text-sky-400 font-medium">Target Monthly Value</span>
          <div className="text-2xl font-black text-sky-400 mt-1">£3,500 - £6,000</div>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-900/60 p-3 rounded-2xl border border-slate-800">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search company or role..."
            className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-3 py-1.5 text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-sky-500"
          />
        </div>

        <div className="flex items-center space-x-2 self-start sm:self-auto overflow-x-auto w-full sm:w-auto">
          {['all', 'Contract', 'Permanent', 'Freelance'].map((t) => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                filterType === t
                  ? 'bg-sky-500 text-white shadow-sm'
                  : 'bg-slate-950 text-slate-400 hover:text-slate-200 border border-slate-800'
              }`}
            >
              {t === 'all' ? 'All Roles' : t}
            </button>
          ))}
        </div>
      </div>

      {/* Kanban Board Columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 items-start">
        {COLUMNS.map((col) => {
          const colApps = filteredApps.filter(a => a.status === col.id);

          return (
            <div key={col.id} className="rounded-2xl bg-slate-900/40 border border-slate-800/80 p-3 space-y-3 min-h-[450px]">
              
              {/* Column Header */}
              <div className={`flex items-center justify-between px-3 py-2 rounded-xl border ${col.border} ${col.color}`}>
                <span className="text-xs font-bold tracking-tight">{col.title}</span>
                <span className="text-xs font-extrabold px-1.5 py-0.5 rounded-full bg-slate-950/60">
                  {colApps.length}
                </span>
              </div>

              {/* Cards List */}
              <div className="space-y-3">
                {colApps.map((app) => (
                  <div
                    key={app.id}
                    className="rounded-xl bg-slate-900 border border-slate-800/90 p-4 space-y-3 hover:border-slate-700 transition-all shadow-md group"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-400">
                          {app.company}
                        </span>
                        <h4 className="text-xs font-bold text-white mt-0.5">{app.role}</h4>
                      </div>
                      <button
                        onClick={() => onDeleteApplication(app.id)}
                        className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-400 transition-opacity p-1"
                        title="Delete application"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="space-y-1 text-[11px] text-slate-300">
                      <div className="flex items-center space-x-1.5 text-emerald-400 font-semibold">
                        <PoundSterling className="w-3.5 h-3.5 shrink-0" />
                        <span>{app.salary_range || 'Rate not listed'}</span>
                      </div>
                      <div className="flex items-center space-x-1.5 text-slate-400">
                        <span className="px-1.5 py-0.2 rounded bg-slate-800 text-[10px]">{app.job_type}</span>
                        <span>•</span>
                        <span>{app.location || 'Remote UK'}</span>
                      </div>
                    </div>

                    {app.notes && (
                      <p className="text-[11px] text-slate-400 bg-slate-950/60 p-2 rounded-lg border border-slate-800/60 line-clamp-2">
                        {app.notes}
                      </p>
                    )}

                    {/* Move Stage Selector & Link */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-800/80 text-[11px]">
                      <select
                        value={app.status}
                        onChange={(e) => handleStatusChange(app, e.target.value as ApplicationStatus)}
                        className="bg-slate-950 border border-slate-800 rounded-lg px-2 py-1 text-[11px] text-slate-300 focus:outline-none focus:border-sky-500"
                      >
                        <option value="target">Target</option>
                        <option value="applied">Applied</option>
                        <option value="interviewing">Interviewing</option>
                        <option value="offer">Offer!</option>
                        <option value="followup">Follow-Up</option>
                      </select>

                      {app.job_url && (
                        <a
                          href={app.job_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-slate-400 hover:text-sky-400 p-1"
                          title="Open job link"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>

                  </div>
                ))}

                {colApps.length === 0 && (
                  <div className="py-8 text-center text-slate-600 text-xs">
                    No roles in this stage
                  </div>
                )}
              </div>

            </div>
          );
        })}
      </div>

      {/* Add Role Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in">
          <div className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <h3 className="text-lg font-bold text-white">Track New Target Job</h3>
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="text-slate-400 hover:text-white text-xs font-semibold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateApplication} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-300">Company Name *</label>
                  <input
                    type="text"
                    required
                    value={newCompany}
                    onChange={(e) => setNewCompany(e.target.value)}
                    placeholder="e.g. FinTech Scaleup"
                    className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-300">Role Title *</label>
                  <input
                    type="text"
                    required
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    placeholder="e.g. Senior Software Engineer"
                    className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-xs text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-300">Salary / Day Rate</label>
                  <input
                    type="text"
                    value={newSalary}
                    onChange={(e) => setNewSalary(e.target.value)}
                    placeholder="e.g. £350/day or £65,000"
                    className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-300">Job Type</label>
                  <select
                    value={newJobType}
                    onChange={(e) => setNewJobType(e.target.value as JobType)}
                    className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-xs text-white"
                  >
                    <option value="Contract">Contract (Outside IR35)</option>
                    <option value="Permanent">Permanent</option>
                    <option value="Freelance">Freelance</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-300">Initial Stage</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value as ApplicationStatus)}
                    className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-xs text-white"
                  >
                    <option value="target">Target / Researching</option>
                    <option value="applied">Applied / Pitched</option>
                    <option value="interviewing">Interviewing</option>
                    <option value="offer">Offer Received</option>
                    <option value="followup">Follow-Up Needed</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-300">Location</label>
                  <input
                    type="text"
                    value={newLocation}
                    onChange={(e) => setNewLocation(e.target.value)}
                    placeholder="e.g. London / Remote UK"
                    className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-xs text-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-300">Job Posting Link / Contact URL</label>
                <input
                  type="url"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="https://..."
                  className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-xs text-white"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-300">Notes / Hiring Manager Name</label>
                <textarea
                  rows={3}
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  placeholder="Key tech stack requirements, interviewer names, or next steps..."
                  className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-xs text-white"
                />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl text-xs font-bold bg-sky-500 hover:bg-sky-400 text-white shadow-lg shadow-sky-500/25"
                >
                  Save to Pipeline
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
